import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Loader2, Building2, CalendarDays, CheckCircle2, Lock, AlertCircle, Send, Save,
  ArrowLeft, GraduationCap, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from './coordinator/AssignMentorDialog';
import CareerMapping from '@/components/internships/CareerMapping';

const GRADES = [9, 10, 11, 12];
const FORMATS = [
  { value: 'on_site', label: 'On-site' },
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
];
const EMPTY = {
  company_name: '', industry: '', website: '', contact_name: '', contact_email: '', contact_phone: '',
  title: '', description: '', student_activities: '', learning_outcomes: '',
  eligible_grades: [], prerequisites: '', format: '', start_date: '', end_date: '',
  daily_start_time: '', daily_end_time: '', positions: '', workplace_address: '',
  supervisor_name: '', supervisor_email: '', supervisor_phone: '', additional_instructions: '',
  career_slug: '', related_career_slugs: [], career_request_name: '', career_request_description: '',
};

const Field = ({ label, required, hint, children }) => (
  <div>
    <Label className="text-[#1F2937] font-medium text-sm">
      {label}{required && <span className="text-[#F97D6A]"> *</span>}
    </Label>
    {children}
    {hint && <p className="text-[11px] text-[#9CA3AF] mt-1">{hint}</p>}
  </div>
);

const inputCls = 'mt-1.5 rounded-xl h-11 border-gray-200';
const areaCls = 'mt-1.5 rounded-xl border-gray-200 min-h-[80px] resize-none';

export default function InternshipOpportunityPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const path = window.location.pathname;
  const mode = path.includes('/internships/invite/') ? 'invite'
    : path.includes('/internships/opportunity/') ? 'edit' : 'browse';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [careers, setCareers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState('');
  const [context, setContext] = useState(null);     // invite / edit context
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);           // { status, edit_link }
  const [mentorMode, setMentorMode] = useState(false);
  const [mentorOppId, setMentorOppId] = useState(null);

  const program = context?.program || programs.find(p => p.id === programId) || null;
  const editable = mode !== 'edit' || context?.editable;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const careerRes = await api.get('/careers/options');
      setCareers(careerRes.data || []);
      if (mode === 'invite') {
        const r = await api.get(`/public/internship-invitations/${token}`);
        setContext(r.data);
        const inv = r.data.invitation || {};
        const existing = r.data.opportunity;
        setForm({
          ...EMPTY,
          company_name: inv.company_name || '', contact_name: inv.contact_name || '',
          contact_email: inv.contact_email || '',
          ...(existing ? cleanForForm(existing) : {}),
        });
        setProgramId(r.data.program?.id || '');
      } else if (mode === 'edit') {
        const r = await api.get(`/public/internship-opportunities/${token}`);
        setContext(r.data);
        setForm({ ...EMPTY, ...cleanForForm(r.data.opportunity) });
        setProgramId(r.data.opportunity?.program_id || '');
      } else {
        let list = [];
        if (user) {
          try {
            const m = await api.get('/mentor/internship-programs');
            if (m.data?.length !== undefined) { list = m.data; setMentorMode(true); }
          } catch { /* not a mentor — fall back to the public list */ }
        }
        if (!mentorMode && !list.length) {
          const r = await api.get('/public/internship-programs');
          list = r.data || [];
        }
        setPrograms(list);
        if (user) {
          setForm(f => ({ ...f, contact_name: user.full_name || '', contact_email: user.email || '' }));
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'This link could not be opened.');
    }
    setLoading(false);
  }, [mode, token, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  function cleanForForm(o) {
    const out = {};
    Object.keys(EMPTY).forEach(k => { out[k] = o?.[k] ?? EMPTY[k]; });
    out.eligible_grades = o?.eligible_grades || [];
    out.related_career_slugs = o?.related_career_slugs || [];
    out.career_request_name = o?.career_request?.name || '';
    out.career_request_description = o?.career_request?.description || '';
    out.positions = o?.positions ?? '';
    return out;
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleGrade = (g) => setForm(f => ({
    ...f,
    eligible_grades: f.eligible_grades.includes(g)
      ? f.eligible_grades.filter(x => x !== g)
      : [...f.eligible_grades, g].sort((a, b) => a - b),
  }));

  const payload = (submit) => ({
    ...form,
    program_id: programId || null,
    positions: form.positions === '' ? null : Number(form.positions),
    eligible_grades: form.eligible_grades,
    submit,
  });

  const save = async (submit) => {
    if (!programId) { toast.error('Choose a school internship programme first'); return; }
    setSaving(true);
    try {
      let res;
      if (mode === 'edit') {
        res = await api.put(`/public/internship-opportunities/${token}`, payload(submit));
      } else if (mode === 'invite') {
        res = context?.opportunity?.id
          ? await api.put(`/public/internship-opportunities/${context.opportunity.edit_link.split('/').pop()}`, payload(submit))
          : await api.post(`/public/internship-opportunities?invitation_token=${token}`, payload(submit));
      } else if (mentorMode) {
        res = mentorOppId
          ? await api.put(`/mentor/internship-opportunities/${mentorOppId}`, payload(submit))
          : await api.post('/mentor/internship-opportunities', payload(submit));
        setMentorOppId(res.data.id);
      } else {
        res = await api.post('/public/internship-opportunities', payload(submit));
      }
      if (submit) {
        setDone(res.data);
      } else {
        toast.success('Draft saved');
        if (mode === 'browse' && !mentorMode && res.data.edit_link) setDone({ ...res.data, draft: true });
        if (mode === 'invite' && !context?.opportunity) setContext(c => ({ ...c, opportunity: res.data }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save this opportunity');
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#9CA3AF]" /></div>;
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="internship-form-error">
        <AlertCircle className="h-10 w-10 text-[#F97D6A] mx-auto mb-3" />
        <h1 className="text-xl font-bold text-[#1F2937] font-['Outfit']">{error}</h1>
        <Button variant="outline" className="rounded-xl mt-5" onClick={() => navigate('/')}>Back to CareersPOV</Button>
      </div>
    );
  }

  if (done && !done.draft) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="internship-submitted">
        <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Submitted to the school</h1>
        <p className="text-sm text-[#6B7280] mt-3 leading-relaxed">
          {done.company_name}'s internship <strong>{done.title}</strong> is now with the careers coordinator for review.
          They will email {done.contact_email} with their decision.
        </p>
        {done.edit_link && (
          <div className="bg-[#F8F9FA] rounded-xl p-4 mt-6 text-left">
            <p className="text-xs font-semibold text-[#1F2937] mb-1.5">Your private link</p>
            <p className="text-[11px] text-[#6B7280] mb-2">Keep this to review or update your submission until the school makes a decision.</p>
            <code className="text-[11px] text-[#4B5563] break-all" data-testid="opportunity-link">{done.edit_link}</code>
          </div>
        )}
      </div>
    );
  }

  const showProgramPicker = mode === 'browse';
  const needsAddress = form.format === 'on_site' || form.format === 'hybrid';

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-10">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-6">
          <Badge className="border-0 bg-[#FFF0ED] text-[#F97D6A] text-[10px] mb-3">Internship opportunity</Badge>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit']">
            {mode === 'edit' ? 'Your internship submission' : 'Offer an internship'}
          </h1>
          <p className="text-sm text-[#6B7280] mt-2 leading-relaxed">
            Short, supervised placements of up to seven days for school students in grades 9–12.
            You can save a draft and finish later.
          </p>
        </div>

        {/* School / programme context */}
        {program && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mb-5" data-testid="program-context">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-[#1F2937] font-['Outfit']">{program.name}</p>
                <p className="text-sm text-[#6B7280] mt-0.5">{context?.school_name || program.school_name}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-[#4B5563]">
                  <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3 text-[#9CA3AF]" />{formatDay(program.start_date)} – {formatDay(program.end_date)}</span>
                  <span className="flex items-center gap-1"><GraduationCap className="h-3 w-3 text-[#9CA3AF]" />Grades {program.eligible_grades?.join(', ')}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-[#9CA3AF]" />Submit by {formatDay(program.application_deadline)}</span>
                </div>
              </div>
              {mode !== 'browse' && (
                <Badge className="border-0 bg-[#F3F4F6] text-[#6B7280] text-[10px] shrink-0" data-testid="locked-badge">
                  <Lock className="h-3 w-3 mr-1" />Set by school
                </Badge>
              )}
            </div>
            {context?.invitation?.note && (
              <p className="text-sm text-[#4B5563] bg-[#F8F9FA] rounded-lg px-3 py-2 mt-3" data-testid="invitation-note">
                “{context.invitation.note}” — {context.invitation.invited_by}
              </p>
            )}
          </div>
        )}

        {mode === 'edit' && context?.opportunity?.status && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mb-5" data-testid="submission-status">
            <p className="text-xs font-semibold text-[#1F2937]">
              Status: <span className="capitalize">{String(context.opportunity.status).replace('_', ' ')}</span>
            </p>
            {context.opportunity.coordinator_note && (
              <p className="text-sm text-[#4B5563] mt-1.5 leading-relaxed" data-testid="coordinator-note">
                School note: “{context.opportunity.coordinator_note}”
              </p>
            )}
            {!editable && <p className="text-xs text-[#9CA3AF] mt-1.5">This submission can no longer be edited.</p>}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 space-y-5">
          {showProgramPicker && (
            <Field label="School internship programme" required hint={mentorMode ? 'Open programmes at your school' : 'Only schools with an open programme are listed'}>
              <Select value={programId} onValueChange={setProgramId}>
                <SelectTrigger className={inputCls} data-testid="program-select"><SelectValue placeholder="Choose a school and programme" /></SelectTrigger>
                <SelectContent>
                  {programs.length === 0 && <SelectItem value="none" disabled>No open programmes right now</SelectItem>}
                  {programs.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.school_name} — {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          <SectionTitle icon={Building2}>Company</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Company name" required>
              <Input value={form.company_name} onChange={e => set('company_name', e.target.value)} className={inputCls} data-testid="f-company-name" />
            </Field>
            <Field label="Industry" required>
              <Input value={form.industry} onChange={e => set('industry', e.target.value)} placeholder="e.g. Software, Healthcare" className={inputCls} data-testid="f-industry" />
            </Field>
            <Field label="Website">
              <Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://" className={inputCls} data-testid="f-website" />
            </Field>
            <Field label="Contact name" required>
              <Input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} className={inputCls} data-testid="f-contact-name" />
            </Field>
            <Field label="Contact email" required hint="The school replies to this address">
              <Input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} className={inputCls} data-testid="f-contact-email" />
            </Field>
            <Field label="Contact phone">
              <Input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} className={inputCls} data-testid="f-contact-phone" />
            </Field>
          </div>

          <SectionTitle icon={GraduationCap}>The internship</SectionTitle>
          <Field label="Internship title" required>
            <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Product Design Taster Week" className={inputCls} data-testid="f-title" />
          </Field>
          <CareerMapping form={form} set={set} careers={careers} status={context?.opportunity?.career_mapping_status} />
          <Field label="Description" required>
            <Textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="What the internship is about" className={areaCls} data-testid="f-description" />
          </Field>
          <Field label="What students will do" required>
            <Textarea value={form.student_activities} onChange={e => set('student_activities', e.target.value)} placeholder="Day-to-day activities, projects, shadowing" className={areaCls} data-testid="f-activities" />
          </Field>
          <Field label="Learning outcomes" required>
            <Textarea value={form.learning_outcomes} onChange={e => set('learning_outcomes', e.target.value)} placeholder="What students take away from the week" className={areaCls} data-testid="f-outcomes" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Eligible grades" required hint={program ? `This programme allows grades ${program.eligible_grades?.join(', ')}` : null}>
              <div className="flex gap-2 mt-1.5">
                {GRADES.map(g => {
                  const allowed = !program || program.eligible_grades?.includes(g);
                  return (
                    <button key={g} type="button" disabled={!allowed} onClick={() => toggleGrade(g)}
                      className={`h-11 flex-1 rounded-xl text-sm font-semibold border transition-colors ${
                        form.eligible_grades.includes(g) ? 'bg-[#FFF0ED] border-[#F97D6A] text-[#F06651]'
                          : allowed ? 'bg-white border-gray-200 text-[#6B7280] hover:border-[#F97D6A]'
                            : 'bg-[#F3F4F6] border-transparent text-[#C8CDD6] cursor-not-allowed'}`}
                      data-testid={`f-grade-${g}`}>
                      {g}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Prerequisites">
              <Input value={form.prerequisites} onChange={e => set('prerequisites', e.target.value)} placeholder="e.g. basic coding, own laptop" className={inputCls} data-testid="f-prerequisites" />
            </Field>
          </div>

          <SectionTitle icon={CalendarDays}>Dates and logistics</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Format" required>
              <Select value={form.format} onValueChange={v => set('format', v)}>
                <SelectTrigger className={inputCls} data-testid="f-format"><SelectValue placeholder="On-site, Remote or Hybrid" /></SelectTrigger>
                <SelectContent>{FORMATS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Positions available" required>
              <Input type="number" min="1" value={form.positions} onChange={e => set('positions', e.target.value)} className={inputCls} data-testid="f-positions" />
            </Field>
            <Field label="Internship start" required hint={program ? `Within ${formatDay(program.start_date)} – ${formatDay(program.end_date)}` : null}>
              <Input type="date" min={program?.start_date} max={program?.end_date} value={form.start_date} onChange={e => set('start_date', e.target.value)} className={inputCls} data-testid="f-start-date" />
            </Field>
            <Field label="Internship end" required hint="Up to 7 days in total">
              <Input type="date" min={form.start_date || program?.start_date} max={program?.end_date} value={form.end_date} onChange={e => set('end_date', e.target.value)} className={inputCls} data-testid="f-end-date" />
            </Field>
            <Field label="Daily start time" required>
              <Input type="time" value={form.daily_start_time} onChange={e => set('daily_start_time', e.target.value)} className={inputCls} data-testid="f-daily-start" />
            </Field>
            <Field label="Daily finish time" required>
              <Input type="time" value={form.daily_end_time} onChange={e => set('daily_end_time', e.target.value)} className={inputCls} data-testid="f-daily-end" />
            </Field>
          </div>
          {needsAddress && (
            <Field label="Workplace address" required>
              <Textarea value={form.workplace_address} onChange={e => set('workplace_address', e.target.value)} placeholder="Where students report each day" className={areaCls} data-testid="f-address" />
            </Field>
          )}

          <SectionTitle icon={CheckCircle2}>Supervision</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Supervisor name" required>
              <Input value={form.supervisor_name} onChange={e => set('supervisor_name', e.target.value)} className={inputCls} data-testid="f-supervisor-name" />
            </Field>
            <Field label="Supervisor email" required>
              <Input type="email" value={form.supervisor_email} onChange={e => set('supervisor_email', e.target.value)} className={inputCls} data-testid="f-supervisor-email" />
            </Field>
            <Field label="Supervisor phone">
              <Input value={form.supervisor_phone} onChange={e => set('supervisor_phone', e.target.value)} className={inputCls} data-testid="f-supervisor-phone" />
            </Field>
          </div>
          <Field label="Additional instructions or requirements">
            <Textarea value={form.additional_instructions} onChange={e => set('additional_instructions', e.target.value)} placeholder="Dress code, ID, safety briefing, anything else the school should know" className={areaCls} data-testid="f-instructions" />
          </Field>

          {editable ? (
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button variant="outline" disabled={saving} onClick={() => save(false)} className="rounded-xl h-11 font-semibold border-gray-200" data-testid="save-draft-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save Draft
              </Button>
              <Button disabled={saving} onClick={() => save(true)} className="rounded-xl h-11 font-semibold bg-[#F97D6A] text-white hover:bg-[#F06651] flex-1" data-testid="submit-opportunity-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Submit to School
              </Button>
            </div>
          ) : (
            <Button variant="outline" className="rounded-xl h-11 w-full" onClick={() => navigate('/')} data-testid="back-home-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />Back to CareersPOV
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 pt-2">
    <Icon className="h-4 w-4 text-[#F97D6A]" />
    <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit']">{children}</h2>
  </div>
);
